import { useEffect } from 'react';
import { useStill } from './useReveal';

/* Cursor-following 3D card tilt with iridescent reflections (owner
   2026-09-30, the "How you start" cards; it replaces their hover lift).

   The pointer's place over the card (-0.5..0.5 across, -0.5..0.5 down) is
   turned into a small tilt: the side under the cursor dips away, like a
   card pressed there. Two layers inside the card follow the same angle: a
   pastel iridescent band that slides against the tilt and a soft white glare
   that sits under the cursor. Everything eases toward the pointer (a
   frame-rate-independent lerp), so the card follows gently and settles back
   flat when the pointer leaves.

   Chromebook rules: only transform and opacity change, written straight to
   the elements in one requestAnimationFrame loop that stops when the card is
   at rest. Mouse and pen only (a touch never tilts); off for reduced motion.

   wrapRef: the untransformed box that takes the pointer (its rect is the
   card's real rect); cardRef: the element that tilts; it holds .cxs-holo. */
const MAX_X = 7; // degrees, top/bottom
const MAX_Y = 9; // degrees, left/right
const EASE = 0.12; // share of the way closed per 60 Hz frame
const clamp = (v) => Math.max(-0.5, Math.min(0.5, v));

export default function useTilt(wrapRef, cardRef) {
  const still = useStill();
  useEffect(() => {
    const wrap = wrapRef.current;
    const card = cardRef.current;
    if (!wrap || !card || !window.matchMedia) return undefined;
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return undefined;
    if (still) return undefined;
    const holo = card.querySelector('.cxs-holo');
    const band = card.querySelector('.cxs-holo-band');
    const glare = card.querySelector('.cxs-holo-glare');

    const cur = { x: 0, y: 0, h: 0 };
    const tgt = { x: 0, y: 0, h: 0 };
    let raf = 0;
    let last = 0;

    const paint = () => {
      const rx = (-cur.y * 2 * MAX_X).toFixed(3);
      const ry = (cur.x * 2 * MAX_Y).toFixed(3);
      card.style.transform = cur.h < 0.001 && Math.abs(cur.x) < 0.001 && Math.abs(cur.y) < 0.001
        ? ''
        : `perspective(1000px) rotateX(${rx}deg) rotateY(${ry}deg) scale(${(1 + 0.015 * cur.h).toFixed(4)})`;
      if (holo) holo.style.opacity = cur.h.toFixed(3);
      // the band slides against the tilt; the glare sits under the cursor
      if (band) band.style.transform = `translate3d(${(-cur.x * 36).toFixed(2)}%, ${(-cur.y * 36).toFixed(2)}%, 0)`;
      if (glare) glare.style.transform = `translate3d(${(cur.x * 50).toFixed(2)}%, ${(cur.y * 50).toFixed(2)}%, 0)`;
    };

    const step = (t) => {
      const dt = last ? Math.min(64, t - last) : 16.7;
      last = t;
      const k = 1 - (1 - EASE) ** (dt / 16.7);
      cur.x += (tgt.x - cur.x) * k;
      cur.y += (tgt.y - cur.y) * k;
      cur.h += (tgt.h - cur.h) * k;
      const done = Math.abs(tgt.x - cur.x) < 0.0008 && Math.abs(tgt.y - cur.y) < 0.0008 && Math.abs(tgt.h - cur.h) < 0.002;
      if (done) {
        cur.x = tgt.x;
        cur.y = tgt.y;
        cur.h = tgt.h;
      }
      paint();
      if (done) {
        raf = 0;
        last = 0;
      } else {
        raf = requestAnimationFrame(step);
      }
    };
    const kick = () => { if (!raf) raf = requestAnimationFrame(step); };

    const onMove = (e) => {
      if (e.pointerType === 'touch') return;
      const r = wrap.getBoundingClientRect();
      if (!r.width || !r.height) return;
      tgt.x = clamp((e.clientX - r.left) / r.width - 0.5);
      tgt.y = clamp((e.clientY - r.top) / r.height - 0.5);
      tgt.h = 1;
      kick();
    };
    const onLeave = () => {
      tgt.x = 0;
      tgt.y = 0;
      tgt.h = 0;
      kick();
    };

    wrap.addEventListener('pointermove', onMove);
    wrap.addEventListener('pointerenter', onMove);
    wrap.addEventListener('pointerleave', onLeave);
    return () => {
      wrap.removeEventListener('pointermove', onMove);
      wrap.removeEventListener('pointerenter', onMove);
      wrap.removeEventListener('pointerleave', onLeave);
      if (raf) cancelAnimationFrame(raf);
      card.style.transform = '';
    };
  }, [wrapRef, cardRef, still]);
}
